import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["useSidePanel", "children"],
  _excluded2 = ["children", "indented", "setIsOpen", "open", "selectedPage", "subHeading", "useBackText", "useSidePanel"];
import { Children, useCallback, useMemo, useState } from 'react';
import styled from 'foundations-theming/utils';
import { Panel } from 'foundations-components/transitional/components/Panel';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import FullScreenModal from './FullScreenModal';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { LeftIcon } from 'foundations-assets';
import { Flex } from 'foundations-components/components/Flex';
import { Small } from 'foundations-components/transitional/components/Small';
import { View } from 'foundations-components/transitional/components/View';
import { PanelHeader } from 'foundations-components/transitional/components/PanelHeader';

// @bend-expect trellis-tokenized
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const ItemsWrapper = styled.div.withConfig({
  displayName: "MobileNestedMenu__ItemsWrapper",
  componentId: "vu1pcf-0"
})(["", " & button:not([data-no-full-width=\"true\"]){width:100%;}> *{padding:0;", " border-bottom:1px solid ", ";}"], props => props.$indented && `margin-left: ${props.theme.space['500']};`, props => props.$indented && `padding: ${props.theme.space['300']} ${props.theme.space['600']} ${props.theme.space['300']} 0;`, props => props.theme.color['border-core-subtle']);
const Header = ({
  useSidePanel,
  children
}) => {
  if (useSidePanel) {
    return /*#__PURE__*/_jsx(PanelHeader, {
      children: children
    });
  }
  return /*#__PURE__*/_jsx(View, {
    backgroundColor: "do-not-use-pantera",
    color: "text-primary-default",
    children: /*#__PURE__*/_jsx(Flex, {
      blockSize: "40px",
      children: children
    })
  });
};
const Container = _ref => {
  let {
      useSidePanel,
      children
    } = _ref,
    fullScreenModalProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  if (useSidePanel) {
    return /*#__PURE__*/_jsx(Panel, {
      width: "100%",
      children: children
    });
  }
  return /*#__PURE__*/_jsx(FullScreenModal, Object.assign({}, fullScreenModalProps, {
    children: children
  }));
};
const MobileNestedMenu = _ref2 => {
  let {
      children,
      indented,
      setIsOpen,
      open,
      selectedPage = -1,
      subHeading,
      useBackText,
      useSidePanel
    } = _ref2,
    restProps = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const [page, showPage] = useState(selectedPage);
  const close = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);
  const goBack = () => {
    showPage(-1);
  };
  const flatChildren = useMemo(() => {
    const allChildren = [];
    Children.toArray(children).forEach(child => {
      const item = child;
      if (item.props.nestedOptions) {
        if (Array.isArray(item.props.children)) {
          allChildren.push(...item.props.children);
        } else {
          allChildren.push(item.props.children);
        }
      } else {
        allChildren.push(child);
      }
    });
    return allChildren;
  }, [children]);
  const pages = useMemo(() => {
    return flatChildren.map(child => {
      if (!child) return '';
      const item = child;
      const {
        props: {
          subpage
        } = {}
      } = item;
      return subpage || '';
    }) || [];
  }, [flatChildren]);
  const header = useMemo(() => {
    return /*#__PURE__*/_jsxs(Header, {
      useSidePanel: useSidePanel,
      children: [page >= 0 && /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "200",
        marginInlineStart: "-400",
        onClick: goBack,
        paddingInline: "400",
        children: [/*#__PURE__*/_jsx(LeftIcon, {
          color: "icon-primary-default",
          size: "300"
        }), ' ', useBackText && /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.common.back"
        })]
      }), /*#__PURE__*/_jsx(DialogCloseButton, {
        "data-test-id": 'nested-menu-close-btn',
        onClick: close
      })]
    });
  }, [close, page, useBackText, useSidePanel]);
  const body = useMemo(() => {
    return (
      /*#__PURE__*/
      // @bend-expect trellis-tokenized
      _jsx(ItemsWrapper, {
        $indented: indented,
        children: page < 0 ? flatChildren.map((child, key) => {
          return child && /*#__PURE__*/_jsx("div", {
            onClick: () => pages[key] && showPage(key),
            role: "menuitem",
            children: child
          }, key);
        }) : pages[page]
      })
    );
  }, [flatChildren, indented, page, pages]);
  return /*#__PURE__*/_jsxs(Container, Object.assign({
    headerContent: useSidePanel ? null : header,
    open: open,
    showCloseButton: false,
    subheader: subHeading ? /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      backgroundColor: "fill-accent-neutral-default",
      blockSize: "35px",
      paddingBlock: "0",
      paddingInline: "500",
      children: /*#__PURE__*/_jsx(Small, {
        color: "text-primary-default",
        children: subHeading
      })
    }) : null,
    useSidePanel: useSidePanel
  }, restProps, {
    children: [useSidePanel && header, body]
  }));
};
export default MobileNestedMenu;